import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Row, Col } from 'react-bootstrap';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import axios from 'axios';

import { getSubscription, getPaymentHistory } from '../../store/actions/payment';
import { getTenant } from '../../store/actions/tenant';
import { successGlobal, errorGlobal } from '../../store/reducers/notifications';
import { WASTE_PLAN_LIMITS, PPWR_PLAN_LIMITS } from '../../config/plans';
import { Loader, getAuthHeader } from '../../utils/tools';

import SubscriptionStatus from './SubscriptionStatus';
import PlanCard from './PlanCard';
import CheckoutModal from './CheckoutModal';
import PaymentHistory from './PaymentHistory';

const stripePromise = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY
    ? loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY)
    : null;

const SectionHeading = ({ title, description }) => (
    <div className="mb-4" style={{ borderBottom: '2px solid #EDF2F7', paddingBottom: '12px' }}>
        <h5 style={{ color: '#0B1157', fontWeight: 700, margin: '0 0 4px' }}>{title}</h5>
        {description && (
            <p style={{ color: '#718094', fontSize: '13px', margin: 0 }}>{description}</p>
        )}
    </div>
);

const Billing = () => {
    const dispatch = useDispatch();
    const { subscriptions, history, loading } = useSelector((state) => state.payment);
    const branchId = useSelector((state) => state.branch.current);

    const wasteSubscription = subscriptions?.waste;
    const ppwrSubscription = subscriptions?.ppwr;

    const [selectedPlan, setSelectedPlan] = useState(null);
    const [selectedModule, setSelectedModule] = useState('waste');
    const [showModal, setShowModal] = useState(false);

    useEffect(() => {
        dispatch(getSubscription('waste'));
        dispatch(getSubscription('ppwr'));
        dispatch(getPaymentHistory());
    }, [dispatch]);

    const handleActivateDemo = async (module) => {
        try {
            await axios.post('/api/tenant/activate-module', { module }, getAuthHeader());
            const moduleName = module === 'ppwr' ? 'PPWR' : 'Evidencia odpadov';
            dispatch(successGlobal(`Demo ${moduleName} bolo aktivované.`));
            dispatch(getSubscription(module));
            dispatch(getTenant(branchId));
        } catch (err) {
            dispatch(errorGlobal(err.response?.data?.message ?? 'Nastala chyba pri aktivácii.'));
        }
    };

    const handleUpgrade = (planKey, module) => {
        if (planKey === 'demo') {
            handleActivateDemo(module);
            return;
        }
        setSelectedPlan(planKey);
        setSelectedModule(module);
        setShowModal(true);
    };

    const handlePaymentSuccess = () => {
        setShowModal(false);
        setSelectedPlan(null);
        dispatch(successGlobal('Platba prebehla úspešne. Váš plán bol aktivovaný.'));
        dispatch(getSubscription('waste'));
        dispatch(getSubscription('ppwr'));
        dispatch(getPaymentHistory());
        dispatch(getTenant(branchId));
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedPlan(null);
    };

    return (
        <div className="content p-4">
            {loading && !wasteSubscription && !ppwrSubscription ? (
                <Loader />
            ) : (
                <>
                    {/* ── EVIDENCIA ODPADOV ── */}
                    <SectionHeading
                        title="Evidencia odpadov"
                        description="Evidencia odpadov, ohlásenie, súhlasy, prevádzkový denník"
                    />

                    <SubscriptionStatus subscription={wasteSubscription} module="waste" />

                    <h6 style={{ color: '#0B1157', fontWeight: 700, marginBottom: '20px', marginTop: '24px' }}>
                        Dostupné plány — Evidencia odpadov
                    </h6>
                    <Row className="g-4 mb-5">
                        {Object.entries(WASTE_PLAN_LIMITS).map(([planKey, planData]) => (
                            <Col key={planKey} xs={12} md={4}>
                                <PlanCard
                                    planKey={planKey}
                                    planData={planData}
                                    module="waste"
                                    currentPlan={wasteSubscription?.plan}
                                    subscriptionStatus={wasteSubscription?.status}
                                    currentPeriodEnd={wasteSubscription?.currentPeriodEnd}
                                    moduleActivated={!!wasteSubscription}
                                    onUpgrade={(key) => handleUpgrade(key, 'waste')}
                                />
                            </Col>
                        ))}
                    </Row>

                    {/* ── PPWR ── */}
                    <SectionHeading
                        title="PPWR"
                        description="Správa obalových jednotiek podľa nariadenia PPWR"
                    />

                    <SubscriptionStatus subscription={ppwrSubscription} module="ppwr" />

                    <h6 style={{ color: '#0B1157', fontWeight: 700, marginBottom: '20px', marginTop: '24px' }}>
                        Dostupné plány — PPWR
                    </h6>
                    <Row className="g-4 mb-5">
                        {Object.entries(PPWR_PLAN_LIMITS).map(([planKey, planData]) => (
                            <Col key={planKey} xs={12} sm={6} md={3}>
                                <PlanCard
                                    planKey={planKey}
                                    planData={planData}
                                    module="ppwr"
                                    currentPlan={ppwrSubscription?.plan}
                                    subscriptionStatus={ppwrSubscription?.status}
                                    currentPeriodEnd={ppwrSubscription?.currentPeriodEnd}
                                    moduleActivated={!!ppwrSubscription}
                                    onUpgrade={(key) => handleUpgrade(key, 'ppwr')}
                                />
                            </Col>
                        ))}
                    </Row>

                    <PaymentHistory history={history} />
                </>
            )}

            {showModal && selectedPlan && (
                <Elements stripe={stripePromise}>
                    <CheckoutModal
                        show={showModal}
                        onHide={handleCloseModal}
                        plan={selectedPlan}
                        module={selectedModule}
                        onSuccess={handlePaymentSuccess}
                    />
                </Elements>
            )}
        </div>
    );
};

export default Billing;
